import React, { useContext, useEffect, useState } from "react";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import { IoBagCheckOutline } from "react-icons/io5";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import Alert from "@mui/material/Alert";

import { MyContext } from "../../App";
import { fetchDataFromApi, postData, deleteData } from "../../utils/api";

import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useDispatch } from "react-redux";
import { fetchCartItems } from "../../store/slices/CartSlice";
import { addOrder } from "../../store/slices/OrderSlice";
import { toast } from "react-toastify";
import AddressManager from "../../Components/AddressManager";

const checkoutStyles = {
  selectedAddressSummary: {
    backgroundColor: "#f8f9fa",
    border: "1px solid #e9ecef",
    borderRadius: "8px",
    padding: "16px",
    marginBottom: "20px",
  },
  addressSummary: {
    fontSize: "14px",
    lineHeight: "1.4",
  },
};

const Checkout = () => {
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [cartData, setCartData] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [paymentMode, setPaymentMode] = useState("full_payment");

  const context = useContext(MyContext);
  const history = useNavigate();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);

    checkLoginStatus();
    context.setEnableFilterTab(false);
    const user = JSON.parse(localStorage.getItem("user"));
    if (user) {
      fetchDataFromApi(`/api/cart?userId=${user?._id}`).then((res) => {
        setCartData(res.data);
        setTotalAmount(
          res.data.length !== 0
            ? res.data
                .map((item) => item.subTotal)
                .reduce((total, value) => total + value, 0)
            : 0
        );
      });
    }
  }, []);

  const checkLoginStatus = () => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/signIn");
      toast.error("Please login to checkout");
    }
  };

  const handleAddressSelect = (address) => {
    setSelectedAddress(address);
  };

  const validateOrder = () => {
    if (!selectedAddress) {
      context.setAlertBox({
        open: true,
        error: true,
        msg: "Please select a delivery address",
      });
      return false;
    }

    if (cartData.length === 0) {
      context.setAlertBox({
        open: true,
        error: true,
        msg: "Your cart is empty",
      });
      return false;
    }

    return true;
  };

  const handlePayment = async (orderData) => {
    const options = {
      key: process.env.REACT_APP_RAZORPAY_KEY_ID,
      amount: orderData.razorpayAmount,
      currency: orderData.currency,
      name: "Sabba Farm",
      description: "Payment for your order",
      order_id: orderData.razorpayOrderId,
      handler: async function (response) {
        try {
          const result = await postData("/api/payment/verify-order", {
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
            paymentHistoryId: orderData.paymentHistoryId,
          });
          if (result.success) {
            context.setAlertBox({
              open: true,
              error: false,
              msg: "Order placed successfully!",
            });
            const user = JSON.parse(localStorage.getItem("user"));
            if (user?._id) {
              await deleteData("/api/cart/clear");
              dispatch(fetchCartItems(user._id));
            }
            dispatch(addOrder(result.order));
            navigate("/orders");
          } else {
            throw new Error(result.message || "Payment verification failed");
          }
        } catch (error) {
          context.setAlertBox({
            open: true,
            error: true,
            msg: error.message || "Payment verification failed",
          });
        }
      },
      prefill: {
        name: selectedAddress.name,
        email: selectedAddress.email,
        contact: selectedAddress.phoneNumber,
      },
      theme: {
        color: "#3399cc",
      },
    };
    const rzp = new window.Razorpay(options);
    rzp.open();
  };

  const checkout = async (e) => {
    e.preventDefault();

    if (!validateOrder()) return;

    try {
      const fullAddress = `${selectedAddress.address}${
        selectedAddress.landmark ? ", " + selectedAddress.landmark : ""
      }, ${selectedAddress.city}, ${selectedAddress.state}`;

      const response = await postData("/api/payment/create-order", {
        shippingDetails: {
          name: selectedAddress.name,
          phoneNumber: selectedAddress.phoneNumber,
          address: fullAddress,
          pincode: selectedAddress.pincode,
          email: selectedAddress.email,
        },
        paymentMode: paymentMode,
      });

      if (response.success) {
        handlePayment(response.data);
      } else {
        context.setAlertBox({
          open: true,
          error: true,
          msg: response.data.message || "Failed to create order",
        });
        return;
      }
    } catch (error) {
      context.setAlertBox({
        open: true,
        error: true,
        msg: error.response.data.message || "Something went wrong",
      });
    }
  };

  return (
    <>
      <Helmet>
        <title>
          Checkout | SabbaFarm (Sabba Farm) - Secure Online Purchase
        </title>

        {/* Meta Description */}
        <meta
          name="description"
          content="Complete your secure checkout on SabbaFarm (also known as Sabba Farm). Review your cart, enter billing details, and place your order with confidence."
        />

        {/* Meta Keywords */}
        <meta
          name="keywords"
          content="SabbaFarm, Sabba Farm, sabbafarm, sabba farm online, secure checkout, order summary, billing details, agri shop checkout, buy agriculture products online, farming tools, seeds, fertilizers"
        />

        {/* Robots Tag */}
        <meta name="robots" content="index, follow" />

        {/* Canonical URL */}
        <link rel="canonical" href="https://sabbafarm.com/checkout" />
      </Helmet>

      <section className="section">
        <div className="container">
          <div className="checkoutForm">
            <div className="row">
              <div className="col-md-8">
                <AddressManager
                  onAddressSelect={handleAddressSelect}
                  selectedAddressId={selectedAddress?.id}
                />
              </div>

              <div className="col-md-4">
                <div className="card orderInfo">
                  <h4 className="hd mb-2 text-base">YOUR ORDER</h4>

                  {selectedAddress && (
                    <div
                      className="selected-address-summary"
                      style={checkoutStyles.selectedAddressSummary}
                    >
                      <h5>Delivery Address</h5>
                      <div
                        className="address-summary"
                        style={checkoutStyles.addressSummary}
                      >
                        <strong>{selectedAddress.name}</strong>
                        <p>{selectedAddress.phoneNumber}</p>
                        <p>
                          {selectedAddress.address}
                          {selectedAddress.landmark &&
                            `, ${selectedAddress.landmark}`}
                        </p>
                        <p>
                          {selectedAddress.city}, {selectedAddress.state} -{" "}
                          {selectedAddress.pincode}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Payment Mode Selection */}
                  <div className="mt-3 mb-3">
                    <FormControl component="fieldset">
                      <FormLabel
                        component="legend"
                        style={{ fontWeight: "bold", marginBottom: "10px" }}
                      >
                        Payment Mode
                      </FormLabel>
                      <RadioGroup
                        value={paymentMode}
                        onChange={(e) => setPaymentMode(e.target.value)}
                      >
                        <FormControlLabel
                          value="full_payment"
                          control={<Radio />}
                          label={
                            <div>
                              <strong>Pay Now (Full Payment)</strong>
                              <div style={{ fontSize: "14px", color: "#666" }}>
                                Pay the full amount online
                              </div>
                            </div>
                          }
                        />
                        <FormControlLabel
                          value="hybrid_cod"
                          control={<Radio />}
                          label={
                            <div>
                              <strong>Cash on Delivery + Online (5%)</strong>
                              <div style={{ fontSize: "14px", color: "#666" }}>
                                Pay 5% online now, rest on delivery
                              </div>
                            </div>
                          }
                        />
                      </RadioGroup>
                    </FormControl>

                    {paymentMode === "hybrid_cod" && (
                      <Alert severity="info" style={{ marginTop: "10px" }}>
                        <strong>Hybrid Payment Mode:</strong>
                        <br />• Pay ₹{Math.round(totalAmount * 0.05)} online now
                        (5% of total)
                        <br />• Pay ₹{Math.round(totalAmount * 0.95)} in cash on
                        delivery
                        <br />• Total: ₹{totalAmount}
                      </Alert>
                    )}
                  </div>
                  <div className="table-responsive mt-3">
                    <table className="table table-borderless">
                      <thead>
                        <tr>
                          <th>Product</th>
                          <th>Total</th>
                        </tr>
                      </thead>

                      <tbody>
                        {cartData?.length !== 0 &&
                          cartData?.map((item, index) => {
                            return (
                              <tr>
                                <td>
                                  {item?.productId.name?.substr(0, 20) + "..."}{" "}
                                  <b>× {item?.quantity}</b>
                                </td>

                                <td>
                                  {item?.subTotal?.toLocaleString("en-US", {
                                    style: "currency",
                                    currency: "INR",
                                  })}
                                </td>
                              </tr>
                            );
                          })}

                        <tr>
                          <td>Subtotal </td>
                          <td>
                            {(cartData?.length !== 0
                              ? cartData
                                  ?.map((item) => item.subTotal)
                                  .reduce((total, value) => total + value, 0)
                              : 0
                            )?.toLocaleString("en-US", {
                              style: "currency",
                              currency: "INR",
                            })}
                          </td>
                        </tr>

                        {paymentMode === "hybrid_cod" && (
                          <>
                            <tr style={{ backgroundColor: "#f8f9fa" }}>
                              <td>Online Payment (5%)</td>
                              <td
                                style={{ color: "#28a745", fontWeight: "bold" }}
                              >
                                ₹{Math.round(totalAmount * 0.05)}
                              </td>
                            </tr>
                            <tr style={{ backgroundColor: "#f8f9fa" }}>
                              <td>Cash on Delivery (95%)</td>
                              <td
                                style={{ color: "#6c757d", fontWeight: "bold" }}
                              >
                                ₹{Math.round(totalAmount * 0.95)}
                              </td>
                            </tr>
                          </>
                        )}

                        <tr style={{ borderTop: "2px solid #dee2e6" }}>
                          <td style={{ fontWeight: "bold" }}>
                            {paymentMode === "hybrid_cod"
                              ? "Total Amount"
                              : "Total"}
                          </td>
                          <td style={{ fontWeight: "bold", fontSize: "18px" }}>
                            ₹{totalAmount}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <Button
                    onClick={checkout}
                    className="btn-blue bg-red btn-lg btn-big"
                    disabled={!selectedAddress}
                  >
                    <IoBagCheckOutline /> &nbsp;
                    {selectedAddress
                      ? paymentMode === "hybrid_cod"
                        ? `Pay ₹${Math.round(totalAmount * 0.05)} Online (5%)`
                        : "Proceed to Payment"
                      : "Select Address to Continue"}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Checkout;
